import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";

const useBoxInventory = ({ storeId, page = 1, pageSize = 10 }) => {
  const { request } = useApi();
  const [boxInventories, setBoxInventories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  const fetchBoxInventory = useCallback(
    async (signal) => {
      if (!storeId) {
        setBoxInventories([]);
        setIsLoading(false);
        setError(null);
        setTotal(0);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await request({
          path: "/box/inventory",
          params: {
            storeId,
            page,
            pageSize,
          },
          headers: { "X-Store-Id": storeId },
          signal,
        });

        // Handle both array response and object with boxInventories property
        let inventories = [];
        if (Array.isArray(data)) {
          inventories = data;
        } else if (Array.isArray(data?.boxInventories)) {
          inventories = data.boxInventories;
        }
        setBoxInventories(inventories);
        setTotal(Number.isFinite(data?.total) ? data.total : inventories.length);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setBoxInventories([]);
        setTotal(0);
        setError(err?.message || "Failed to load box inventory.");
      } finally {
        setIsLoading(false);
      }
    },
    [request, storeId, page, pageSize]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchBoxInventory(controller.signal);
    return () => controller.abort();
  }, [fetchBoxInventory]);

  const refetch = useCallback(() => {
    const controller = new AbortController();
    fetchBoxInventory(controller.signal);
    return () => controller.abort();
  }, [fetchBoxInventory]);

  // Map box inventory data to format expected by BoxInventory component
  const mappedInventory = boxInventories.map((item) => {
    // Convert boxTypeName to "Type A", "Type B", etc.
    // Handle both "A" and "BOX_TYPE_1" formats
    let typeLabel = item.boxTypeName || "";
    
    // If it's just a single letter (A, B, C), prepend "Type "
    if (/^[ABC]$/i.test(typeLabel)) {
      typeLabel = `Type ${typeLabel.toUpperCase()}`;
    } else if (typeLabel.includes("BOX_TYPE_1")) {
      typeLabel = "Type A";
    } else if (typeLabel.includes("BOX_TYPE_2")) {
      typeLabel = "Type B";
    } else if (typeLabel.includes("BOX_TYPE_3")) {
      typeLabel = "Type C";
    } else {
      // Fallback: try to extract type from name
      const match = typeLabel.match(/TYPE[_\s]*([ABC123])/i);
      if (match) {
        typeLabel = `Type ${match[1].toUpperCase()}`;
      } else if (typeLabel) {
        // If we have a boxTypeName but it doesn't match patterns, just prepend "Type "
        typeLabel = `Type ${typeLabel}`;
      }
    }

    return {
      type: typeLabel,
      current: Number.isFinite(item.quantity) ? item.quantity : 0,
      total: Number.isFinite(item.threshold) ? item.threshold : 0,
      boxTypeName: item.boxTypeName,
      boxReplenishmentAck: item.boxReplenishmentAck,
    };
  });

  return {
    boxInventories,
    inventory: mappedInventory,
    isLoading,
    error,
    total,
    refetch,
  };
};

export default useBoxInventory;
